import { FACEBOOK_CAPI_CONFIG, FACEBOOK_EVENTS, EVENT_SOURCES, CONTENT_TYPES, DEFAULT_CURRENCY, DELIVERY_CATEGORIES } from './constants';
import { prepareUserData, formatOrderItems, getCurrentCurrency, getEventTimestamp, getEventSourceUrl, formatErrorResponse } from './utils';
import UTMTracker from '../UTMTracker';

class FacebookCAPIService {
    static #instance = null;

    constructor() {
        if (FacebookCAPIService.#instance) {
            return FacebookCAPIService.#instance;
        }
        FacebookCAPIService.#instance = this;
    }

    /**
     * Send event to Facebook CAPI (all pixels)
     * @param {string} eventName - Name of the event
     * @param {Object} userData - User data
     * @param {Object} customData - Custom event data
     * @returns {Promise} - API response
     */
    async sendEvent(eventName, userData, customData = {}) {
        try {
            const eventData = {
                event_name: eventName,
                event_time: getEventTimestamp(),
                action_source: EVENT_SOURCES.WEBSITE,
                event_source_url: getEventSourceUrl(),
                user_data: prepareUserData(userData),
                custom_data: customData
            };

            // Send to all configured pixels
            const promises = FACEBOOK_CAPI_CONFIG.PIXELS.map(async (pixel) => {
                const response = await fetch(`${FACEBOOK_CAPI_CONFIG.getBaseUrl(pixel.PIXEL_ID)}?access_token=${pixel.ACCESS_TOKEN}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ data: [eventData] })
                });

                const result = await response.json();

                console.log(`Facebook CAPI ${eventName} event response (${pixel.NAME}):`, {
                    pixel: pixel.PIXEL_ID,
                    result: result
                });

                return { pixel: pixel.PIXEL_ID, result };
            });

            const results = await Promise.all(promises);
            return { results };
        } catch (error) {
            console.error(`Facebook CAPI ${eventName} event error:`, error);
            return formatErrorResponse(error, eventName);
        }
    }

    /**
     * Track purchase event
     * @param {Object} orderData - Order data
     * @param {Object} userData - User data
     * @returns {Promise} - API response
     */
    async trackPurchase(orderData, userData) {
        try {
            
            // Format order items properly for contents array (without name field)
            const items = formatOrderItems(orderData.new_ordered_items || orderData.items || []);
            
            // Get product names for content_name field (separate from contents array)
            const productNames = (orderData.new_ordered_items || orderData.items || []).map(
                item => item.subscription_plan?.plan_name || item.name || 'Unknown Product'
            );
            
            // Get UTM parameters for analytics
            const utmParams = UTMTracker.getUTMForAnalytics();
                
            // Prepare event data with correct contents structure
            const eventData = {
                event_name: FACEBOOK_EVENTS.PURCHASE,
                event_time: getEventTimestamp(),
                action_source: EVENT_SOURCES.WEBSITE,
                event_source_url: getEventSourceUrl(),
                event_id: `purchase_${orderData.order_id || orderData.id}_${Date.now()}`,
                
                user_data: prepareUserData({
                    ...userData,
                    clientIp: userData.clientIp || '',
                }),
                
                custom_data: {
                    content_type: CONTENT_TYPES.PRODUCT,
                    content_ids: items.map(item => item.id),
                    content_name: productNames.join(', '), // This is separate from contents array
                    contents: items, // This now only has id, quantity, item_price
                    currency: orderData.details?.currency_type || orderData.currency_type || DEFAULT_CURRENCY,
                    num_items: items.length,
                    value: orderData.payment?.amount || orderData.total_amount || orderData.amount || 0,
                    order_id: orderData.order_id || orderData.id,
                    predicted_ltv: orderData.payment?.amount || orderData.total_amount || orderData.amount || 0,
                    status: orderData.status || 'completed',
                    payment_method: orderData.payment?.payment_method || orderData.payment_method,
                    discount_amount: orderData.amount_saved_dict?.amount_saved || orderData.discount || 0,
                    ...utmParams
                }
            };

            // Note: Client-side Facebook Pixel Purchase event is handled by analyticsService
            // This prevents duplicate events while keeping server-side CAPI tracking
            
            // Send server-side event to all configured pixels
            const promises = FACEBOOK_CAPI_CONFIG.PIXELS.map(async (pixel) => {
                const response = await fetch(`${FACEBOOK_CAPI_CONFIG.getBaseUrl(pixel.PIXEL_ID)}?access_token=${pixel.ACCESS_TOKEN}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({data: [eventData]})
                });

                const result = await response.json();

                console.log(`Facebook CAPI Purchase event response (${pixel.NAME}):`, {
                    pixel: pixel.PIXEL_ID,
                    result: result
                });

                return { pixel: pixel.PIXEL_ID, result };
            });

            const results = await Promise.all(promises);
            return { results };
        } catch (error) {
            console.error(`Facebook CAPI Purchase event error:`, error);
            return formatErrorResponse(error, FACEBOOK_EVENTS.PURCHASE);
        }
    }

    /**
     * Track login event
     * @param {Object} userData - User data
     * @returns {Promise} - API response
     */
    async trackLogin(userData) {
        return this.sendEvent(FACEBOOK_EVENTS.LOGIN, userData, {
            method: userData.method || 'phone_otp',
            success: true,
            currency_type: getCurrentCurrency()
        });
    }

    /**
     * Track add to cart event
     * @param {Object} product - Product data
     * @param {Object} userData - User data
     * @returns {Promise} - API response
     */
    async trackAddToCart(product, userData) {
        
        // Original Facebook tracking
        return this.sendEvent(FACEBOOK_EVENTS.ADD_TO_CART, userData, {
            content_type: CONTENT_TYPES.PRODUCT,
            content_ids: [product.subscription_plan_id],
            content_name: product.plan_name,
            currency: product.currency_type || DEFAULT_CURRENCY,
            value: product.discounted_cost || 0
        });
    }

    /**
     * Track initiate checkout event
     * @param {Object} cartData - Cart data
     * @param {Object} userData - User data
     * @returns {Promise} - API response
     */
    async trackInitiateCheckout(cartData, userData) {
        return this.sendEvent(FACEBOOK_EVENTS.INITIATE_CHECKOUT, userData, {
            currency: cartData.currency_type || DEFAULT_CURRENCY,
            value: cartData.total_amount || 0,
            num_items: cartData.items?.length || 0,
            content_ids: cartData.items?.map(item => item.subscription_plan?.subscription_plan_id)
        });
    }

    /**
     * Track payment event
     * @param {Object} paymentData - Payment data
     * @param {Object} userData - User data
     * @returns {Promise} - API response
     */
    async trackPayment(paymentData, userData) {
        return this.sendEvent(FACEBOOK_EVENTS.PAYMENT, userData, {
            status: paymentData.status,
            payment_id: paymentData.payment_id,
            order_id: paymentData.order_id,
            value: paymentData.amount,
            currency: paymentData.currency || DEFAULT_CURRENCY
        });
    }

    /**
     * Track view content event
     * @param {Object} contentData - Content data
     * @param {Object} userData - User data
     * @returns {Promise} - API response
     */
    async trackViewContent(contentData, userData) {
        return this.sendEvent(FACEBOOK_EVENTS.VIEW_CONTENT, userData, {
            content_type: contentData.content_type || CONTENT_TYPES.PRODUCT,
            content_ids: contentData.content_ids,
            content_name: contentData.content_name,
            content_category: contentData.content_category,
            currency: contentData.currency || DEFAULT_CURRENCY,
            value: contentData.value || 0
        });
    }
}

// Export singleton instance
export const facebookCAPI = new FacebookCAPIService(); 